// Supervises the opponent's worker: asks it to think, and kills it when it
// stops answering.
//
// The policy lives here rather than in the hook so it can be tested without a
// browser. `spawn` and the clock are injected; the tests drive a fake worker
// that answers immediately, answers late, or never answers at all — the last
// being the case that matters, since a planner that hangs takes the game with
// it.

import type { Command, GameState } from "src/engine/rules/commands";
import type { Level, Tuning } from "./difficulty";
import type { BeltAges, Task } from "./tasks";
import type { Weights } from "./tuning";
import { looseTurn, naiveTurn } from "./naive";

/**
 * Failed plans in a row before the planner stops being careful.
 *
 * Three, because one is noise and two could be a slow machine — and because
 * the thing on the other side of this number is a game that has stopped, which
 * is worth escaping with a worse move rather than sitting in with a better one
 * that never arrives.
 */
const LOOSE_AFTER = 3;

/** How long a turn's thinking may take before the worker is presumed hung. */
export const PLAN_DEADLINE_MS = 1500;
/** How long an idle worker may take to answer a ping before it is replaced. */
export const PING_DEADLINE_MS = 750;

/**
 * The bit of a Worker this needs. Narrow on purpose: it is what lets the tests
 * hand it a fake that answers late, or not at all, without a browser.
 */
export interface PlannerPort {
  postMessage(message: unknown): void;
  terminate(): void;
  onmessage: ((event: MessageEvent<unknown>) => void) | ((event: { data: unknown }) => void) | null;
}

export interface PlannerOptions {
  spawn: () => PlannerPort;
  /** Injected so tests need neither real timers nor a real worker. */
  setTimeout?: (fn: () => void, ms: number) => number;
  clearTimeout?: (handle: number) => void;
  /** Told when the worker had to be killed, so the UI can say so. */
  onRestart?: (reason: "timeout" | "error") => void;
}

export interface PlanOutcome {
  commands: Command[];
  /**
   * What the opponent is still in the middle of, to hand back next turn.
   *
   * **Absent on a fallback**, which is not the same as empty. The plan lives
   * in the main thread precisely so a worker dying does not lose it — and a
   * fallback that reported an empty list would have the driver overwrite its
   * own memory with nothing, which is the failure this design exists to
   * prevent, arriving through the door marked "handled".
   */
  tasks?: Task[];
  /**
   * Thinking to carry into the next turn. See `searchDepth`.
   *
   * **Absent on a fallback for the same reason `tasks` is**, and the reason is
   * sharper here: a fallback happens because a plan ran long, so a bank the
   * driver reset on one would leave a slow machine perpetually broke and a
   * fast one able to save — the depth would depend on how quickly the client
   * runs, which is exactly what the determinism rule forbids. Undefined means
   * "keep what you had".
   */
  bank?: number;
  /**
   * How long each dead-ended run had sat unextended. See `ageBelts`.
   *
   * **Absent on a fallback**, exactly as `tasks` and `bank` are: a fallback
   * knows nothing about what the plan saw, and reporting an empty map would
   * have the driver forget how long every lane had been sitting there — which
   * hands a slow machine a network that never gets tidied.
   */
  beltAges?: BeltAges;
  /**
   * Which stance proposed the turn that won, and what the judge scored it.
   *
   * **Absent on a fallback, and absent is a claim** — the same reading `tasks`
   * and `bank` take, sharpened: `naiveTurn` and `looseTurn` compare nothing, so
   * a stance name on one would be a fiction about a decision that was never
   * made. What a reader should conclude from nothing here is that nothing was
   * judged, which is exactly true of a fallback, a patrol's own bookkeeping and
   * a turn with an empty pool.
   *
   * Reported rather than used. Nothing in the planner or the driver reads it —
   * it exists because a move on the board cannot afterwards say why it was
   * played, since the candidates it beat are gone and the winner looks like any
   * other move.
   */
  stance?: string;
  value?: number;
  /** True when the worker failed and this is the fallback move. */
  fallback: boolean;
}

export class OpponentPlanner {
  private port: PlannerPort | null = null;
  private nextId = 1;
  /**
   * Plans that have failed back to back.
   *
   * One failure is a worker having a bad turn and `naiveTurn` covers it. Three
   * in a row is the *position* systematically breaking the planner, and
   * answering it with the same careful fallback a fourth time is how a game
   * sits there refusing to continue. Reset by any plan that lands.
   */
  private failures = 0;
  private pending: {
    id: number;
    resolve: (outcome: PlanOutcome) => void;
    timer: number;
    state: GameState;
    side: number;
  } | null = null;

  private readonly setTimer: (fn: () => void, ms: number) => number;
  private readonly clearTimer: (handle: number) => void;

  constructor(private options: PlannerOptions) {
    this.setTimer = options.setTimeout ?? ((fn, ms) => window.setTimeout(fn, ms));
    this.clearTimer = options.clearTimeout ?? ((handle) => window.clearTimeout(handle));
  }

  /**
   * Think about a turn.
   *
   * Always resolves, and always with something playable. A planner that could
   * reject would put the decision of what to do about it at every call site,
   * and the answer is the same everywhere: play the simple move and carry on.
   */
  plan(
    state: GameState,
    side: number,
    level: Level,
    tuning?: Tuning,
    /** What this side was doing last turn. See `ai/tasks.ts`. */
    tasks: readonly Task[] = [],
    /** The play profile's evaluation overrides — per side, see `profiles.ts`. */
    weights?: Partial<Weights>,
    /** Thinking saved from earlier turns. See `searchDepth`. */
    bank = 0,
    /** How long each dead-ended run has sat unextended. See `ageBelts`. */
    beltAges: BeltAges = new Map()
  ): Promise<PlanOutcome> {
    // One at a time. A second request while the first is outstanding means the
    // turn moved on without us, so the stale one is abandoned rather than
    // allowed to answer late into a position it no longer describes.
    if (this.pending) this.abandon();

    return new Promise<PlanOutcome>((resolve) => {
      const port = this.ensurePort();
      const id = this.nextId++;
      const timer = this.setTimer(() => this.giveUp("timeout"), PLAN_DEADLINE_MS);
      this.pending = { id, resolve, timer, state, side };
      port.postMessage({
        type: "plan",
        id,
        state,
        side,
        level,
        tuning,
        weights,
        tasks,
        bank,
        beltAges
      });
    });
  }

  /** Check an idle worker is still answering, replacing it if not. */
  ping(): void {
    if (this.pending || !this.port) return;
    const id = this.nextId++;
    const timer = this.setTimer(() => {
      this.replacePort("timeout");
    }, PING_DEADLINE_MS);
    const port = this.port;
    const answered = (event: { data: unknown }) => {
      const data = event.data as { type?: string; id?: number };
      if (data?.type === "pong" && data.id === id) {
        this.clearTimer(timer);
        port.onmessage = (next: { data: unknown }) => this.receive(next);
      }
    };
    port.onmessage = answered;
    port.postMessage({ type: "ping", id });
  }

  dispose(): void {
    if (this.pending) this.clearTimer(this.pending.timer);
    this.pending = null;
    this.port?.terminate();
    this.port = null;
  }

  private ensurePort(): PlannerPort {
    if (this.port) return this.port;
    const port = this.options.spawn();
    port.onmessage = (event: { data: unknown }) => this.receive(event);
    this.port = port;
    return port;
  }

  private receive(event: { data: unknown }): void {
    const data = event.data as {
      type?: string;
      id?: number;
      commands?: Command[];
      tasks?: Task[];
      bank?: number;
      beltAges?: BeltAges;
      stance?: string;
      value?: number;
    };
    if (data?.type !== "plan") return;

    const pending = this.pending;
    // A reply to a request we have already given up on: the worker came back
    // after being replaced, or after the turn moved on. Its answer describes a
    // position that is no longer on the board, so it is dropped.
    if (!pending || pending.id !== data.id) return;

    this.clearTimer(pending.timer);
    this.pending = null;
    this.failures = 0;
    pending.resolve({
      commands: data.commands ?? [],
      tasks: (data.tasks ?? []) as Task[],
      bank: data.bank,
      beltAges: data.beltAges,
      stance: data.stance,
      value: data.value,
      fallback: false
    });
  }

  /** The worker failed to answer: kill it, replace it, and play something. */
  private giveUp(reason: "timeout" | "error"): void {
    const pending = this.pending;
    this.pending = null;
    this.replacePort(reason);
    if (!pending) return;
    // No tasks on a fallback. The planner failed, so it has no idea what was
    // under way, and carrying a stale job past the turn that broke it is how a
    // recovered opponent goes straight back to being wrong.
    // No `tasks` field at all: the worker never answered, so it has nothing to
    // say about what was under way, and the driver keeps what it already had.
    this.failures++;
    const commands =
      this.failures >= LOOSE_AFTER
        ? // The attempt count, so each retry takes a *different* step while two
          // runs that fail the same way still play the same move.
          looseTurn(pending.state, pending.side, this.failures)
        : naiveTurn(pending.state, pending.side);
    pending.resolve({ commands, fallback: true });
  }

  private abandon(): void {
    if (!this.pending) return;
    this.clearTimer(this.pending.timer);
    const pending = this.pending;
    this.pending = null;
    pending.resolve({ commands: [], fallback: false });
  }

  private replacePort(reason: "timeout" | "error"): void {
    this.port?.terminate();
    this.port = null;
    this.options.onRestart?.(reason);
  }
}
